import React, { useState, useEffect, useMemo } from "react";
import Autocomplete from "@mui/material/Autocomplete";
import {
  Box, Button, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, IconButton, TextField, InputAdornment, MenuItem,
  Select, FormControl, InputLabel, Chip, Avatar, Dialog, DialogTitle,
  DialogContent, DialogActions, Divider, Tooltip, Stack, CircularProgress,
  useTheme, TableSortLabel
} from "@mui/material";
import { Helmet } from "react-helmet";
import SearchIcon from "@mui/icons-material/Search";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PersonRemoveIcon from "@mui/icons-material/PersonRemove";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CloseIcon from "@mui/icons-material/Close";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import BusinessIcon from "@mui/icons-material/Business";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import BadgeIcon from "@mui/icons-material/Badge";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Grid from "@mui/material/Grid";
import GroupIcon from "@mui/icons-material/Group";
import DOMPurify from "dompurify";

import { useUser } from "../context/UserContext";
import SectionLoader from "../components/SectionLoader";
import Toast from "../components/Toast";
import ConfirmDialog from "../components/ConfirmDialog";

function formatDate(dateStr) {
  if (!dateStr) return "N/A";
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return "N/A";
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

function formatTimeSlot(slot) {
  if (!slot) return "N/A";
  if (typeof slot === "string") return slot;
  if (slot.from_slot && slot.to_slot) {
    return `${slot.from_slot} - ${slot.to_slot}`;
  }
  return slot.from_slot || slot.to_slot || "N/A";
}

const isSubscriberActive = (sub) => {
  if (!sub) return false;
  if (sub.is_subscribed !== undefined) return Boolean(sub.is_subscribed);
  if (sub.deleted_at) return false;
  if (sub.sub_conf === "no") return false;
  if (sub.status === "Unsubscribed" || sub.status === "Declined" || sub.status === "inactive") return false;
  return true;
};

function StatCard({ label, count, IconComponent, active = false, onClick }) {
  const theme = useTheme();
  const isLight = theme.palette.mode === 'light';

  const bgDefault = isLight ? '#FFFFFF' : 'rgba(255,255,255,0.05)';
  const bgActive = isLight
    ? 'linear-gradient(135deg, rgba(13, 148, 136, 0.12) 0%, rgba(13, 148, 136, 0.03) 100%)'
    : 'linear-gradient(135deg, rgba(76, 161, 175, 0.28) 0%, rgba(44, 62, 80, 0.5) 100%)';
  const borderDefault = isLight ? '1px solid #E2E8F0' : '1px solid rgba(255,255,255,0.1)';
  const borderActive = isLight ? '2px solid #0D9488' : '2px solid #4CA1AF';
  const countColor = isLight ? (active ? '#0D9488' : '#0F172A') : '#FFFFFF';
  const labelColor = isLight ? (active ? '#0F172A' : '#64748B') : (active ? '#FFFFFF' : 'rgba(255,255,255,0.7)');
  const iconBg = active ? (isLight ? '#0D9488' : '#4CA1AF') : (isLight ? 'rgba(13, 148, 136, 0.1)' : 'rgba(76, 161, 175, 0.2)');
  const iconColor = active ? '#FFFFFF' : (isLight ? '#0D9488' : '#4CA1AF');

  return (
    <Card
      onClick={onClick}
      variant="outlined"
      sx={{
        height: "100%",
        minWidth: { xs: '100%', sm: 180 },
        width: "100%",
        borderRadius: 2.5,
        cursor: onClick ? 'pointer' : 'default',
        userSelect: 'none',
        transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
        background: active ? bgActive : bgDefault,
        backdropFilter: 'blur(20px)',
        border: active ? borderActive : borderDefault,
        boxShadow: active
          ? (isLight ? '0 4px 16px rgba(13, 148, 136, 0.18)' : '0 8px 24px rgba(76, 161, 175, 0.35)')
          : (isLight ? '0 1px 3px rgba(0, 0, 0, 0.05)' : '0 2px 8px rgba(0, 0, 0, 0.1)'),
        position: 'relative',
        overflow: 'hidden',
        '&:hover': onClick ? {
          transform: 'translateY(-3px)',
          borderColor: active ? borderActive : (isLight ? '#0D9488' : 'rgba(76, 161, 175, 0.5)'),
          background: active
            ? bgActive
            : (isLight ? '#FFFFFF' : 'rgba(76, 161, 175, 0.12)'),
          boxShadow: isLight ? '0 8px 20px rgba(15, 23, 42, 0.08)' : '0 8px 20px rgba(0, 0, 0, 0.3)',
        } : {},
      }}
    >
      <CardContent sx={{ minHeight: 80, p: '16px !important' }}>
        <Box display="flex" alignItems="center" sx={{ width: "100%" }}>
          <Box
            sx={{
              height: 48,
              width: 48,
              background: iconBg,
              borderRadius: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mr: 2,
              flexShrink: 0,
              transition: 'all 0.25s ease',
              boxShadow: active ? (isLight ? '0 4px 12px rgba(13, 148, 136, 0.25)' : '0 4px 12px rgba(76, 161, 175, 0.4)') : 'none',
            }}
          >
            <IconComponent sx={{ fontSize: 28, color: iconColor }} />
          </Box>

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box display="flex" alignItems="center" justifyContent="space-between" gap={1}>
              <Typography variant="h5" fontWeight="bold" noWrap sx={{ color: countColor }}>
                {count}
              </Typography>
              {active && (
                <Box
                  sx={{
                    px: 1,
                    py: 0.2,
                    borderRadius: 1,
                    bgcolor: isLight ? 'rgba(13, 148, 136, 0.12)' : 'rgba(76, 161, 175, 0.3)',
                    border: isLight ? '1px solid rgba(13, 148, 136, 0.35)' : '1px solid rgba(76, 161, 175, 0.6)',
                    color: isLight ? '#0D9488' : '#64d2e2',
                    fontSize: '0.65rem',
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                  }}
                >
                  Filter
                </Box>
              )}
            </Box>
            <Typography variant="body2" noWrap sx={{ color: labelColor, fontWeight: active ? 600 : 400 }}>
              {label}
            </Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}

const Subscribers = ({ hideHeader = false }) => {
  const muiTheme = useTheme();
  const isLight = muiTheme.palette.mode === 'light';
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all"); // 'all', 'active', 'unsubscribed'
  const [subscribers, setSubscribers] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [organization, setOrganization] = useState(null);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState({});

  // Detail Modal State
  const [selectedSubscriber, setSelectedSubscriber] = useState(null);
  const [viewModalOpen, setViewModalOpen] = useState(false);

  // Confirmation Dialog State
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: "",
    message: "",
    onConfirm: null,
    severity: "warning",
    confirmText: "Confirm",
  });

  // Toast feedback state
  const [toast, setToast] = useState({ open: false, message: "", severity: "success" });
  const [subscriberCounts, setSubscriberCounts] = useState({ all: 0, active: 0, unsubscribed: 0, thisMonth: 0 });
  const [sortOrder, setSortOrder] = useState('asc');

  const sortedSubscribers = useMemo(() => {
    return [...subscribers].sort((a, b) => {
      const nameA = `${a.app_users?.first_name || ''} ${a.app_users?.last_name || ''}`.trim().toLowerCase();
      const nameB = `${b.app_users?.first_name || ''} ${b.app_users?.last_name || ''}`.trim().toLowerCase();
      if (sortOrder === 'asc') return nameA.localeCompare(nameB);
      if (sortOrder === 'desc') return nameB.localeCompare(nameA);
      return new Date(b.created_at || 0) - new Date(a.created_at || 0);
    });
  }, [subscribers, sortOrder]);

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000/api/admin/';
  const orgOptions = [{ id: 0, org_name: "All Organizations" }, ...organizations];
  const { user, isSwitched, effectiveOrgId, orgViewMode } = useUser();
  const isSwitchedOrgMode = Boolean(
    isSwitched ||
    orgViewMode ||
    effectiveOrgId ||
    user?.is_switched_from_super_admin ||
    sessionStorage.getItem('gpn_sa_token') ||
    localStorage.getItem('gpn_sa_token') ||
    sessionStorage.getItem('gpn_org_view_mode') ||
    localStorage.getItem('gpn_org_view_mode')
  );
  const showOrganizationColumn = Boolean(user?.role === 1 && !isSwitchedOrgMode && !user?.org_id);

  // Load organizations
  useEffect(() => {
    const fetchRolesAndOrganizations = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${API_URL}roles-organizations`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        const data = await response.json();
        if (!response.ok || data.success === false) {
          if (data.message === 'Invalid token') {
            localStorage.removeItem('token');
            window.location.href = '/login';
            return;
          }
        }
        if (data.organizations) {
          setOrganizations(data.organizations);
        }
      } catch (err) {
        console.error("Error loading organizations:", err);
      }
    };
    fetchRolesAndOrganizations();
  }, [API_URL]);


  useEffect(() => {
    const fetchSubscriberCounts = async () => {
      try {
        const token = localStorage.getItem('token');
        const targetOrg = effectiveOrgId || (organization?.id !== 0 ? organization?.id : null);
        const res = await fetch(`${API_URL}prayer-subscriber-list`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ organization: targetOrg || null, page: 1, pageSize: 500 })
        });
        const data = await res.json();
        const list = data.data || [];
        const active = list.filter(s => isSubscriberActive(s)).length;
        const unsub = list.filter(s => !isSubscriberActive(s)).length;
        const now = new Date();
        const thisMonth = list.filter(s => {
          if (!s.created_at) return false;
          const d = new Date(s.created_at);
          return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
        }).length;
        setSubscriberCounts({
          all: data.totalCount || list.length,
          active,
          unsubscribed: unsub,
          thisMonth
        });
      } catch (err) {
        console.error("Error fetching subscriber counts:", err);
      }
    };
    fetchSubscriberCounts();
  }, [effectiveOrgId, organization]);

  // Load subscribers
  const fetchUsers = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const targetOrg = effectiveOrgId || (organization?.id !== 0 ? organization?.id : null);
      const response = await fetch(`${API_URL}prayer-subscriber-list`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          search: search,
          organization: targetOrg || null,
          status: statusFilter !== 'all' ? statusFilter : undefined,
          page: page,
          pageSize: pageSize,
          fromDate: fromDate || undefined,
          toDate: toDate || undefined
        })
      });
      const data = await response.json();

      if (!response.ok || data.success === false) {
        if (data.message === 'Invalid token') {
          localStorage.removeItem('token');
          window.location.href = '/login';
          return;
        }
      }

      const list = data.data || [];
      setSubscribers(list);
      setTotalItems(data.totalCount || list.length);
      setTotalPages(data.totalPages || Math.ceil((data.totalCount || list.length) / pageSize) || 1);
    } catch (err) {
      console.error("Error fetching subscribers:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, [organization, search, statusFilter, page, pageSize, fromDate, toDate]);

  // Toggle or Update Subscription Status
  const handleToggleSubscription = async (subscriber, targetAction) => {
    const currentActive = isSubscriberActive(subscriber);
    const action = targetAction || (currentActive ? "unsubscribe" : "subscribe");
    const userName = `${subscriber.app_users?.first_name || ""} ${subscriber.app_users?.last_name || ""}`.trim() || subscriber.app_users?.email || "this user";
    const prayerName = subscriber.session_prayers?.title || "Prayer Closet";

    setConfirmDialog({
      open: true,
      title: action === "unsubscribe" ? "Confirm Unsubscribe" : "Confirm Re-subscribe",
      message: action === "unsubscribe"
        ? `Are you sure you want to unsubscribe ${userName} from "${prayerName}"? They will no longer be registered in this prayer closet commitment.`
        : `Are you sure you want to re-subscribe ${userName} to "${prayerName}"?`,
      severity: action === "unsubscribe" ? "warning" : "info",
      confirmText: action === "unsubscribe" ? "Unsubscribe" : "Re-subscribe",
      onConfirm: async () => {
        setConfirmDialog(prev => ({ ...prev, open: false }));
        setActionLoading(prev => ({ ...prev, [subscriber.id]: true }));

        try {
          const token = localStorage.getItem('token');
          const response = await fetch(`${API_URL}update-prayer-subscription-status`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              id: subscriber.id,
              action: action,
              sub_conf: action === "unsubscribe" ? "no" : "yes"
            })
          });

          const result = await response.json();

          if (response.ok && result.success !== false) {
            setToast({
              open: true,
              message: result.message || `User successfully ${action === "unsubscribe" ? "unsubscribed from" : "subscribed to"} Prayer Closet.`,
              severity: "success"
            });
            // Update local state smoothly
            setSubscribers(prev => prev.map(s => {
              if (s.id === subscriber.id) {
                return {
                  ...s,
                  sub_conf: action === "unsubscribe" ? "no" : "yes",
                  deleted_at: action === "unsubscribe" ? new Date().toISOString() : null,
                  status: action === "unsubscribe" ? "Unsubscribed" : "Active",
                  is_subscribed: action !== "unsubscribe"
                };
              }
              return s;
            }));

            // If detail modal is open for this subscriber, update it too
            if (selectedSubscriber && selectedSubscriber.id === subscriber.id) {
              setSelectedSubscriber(prev => ({
                ...prev,
                sub_conf: action === "unsubscribe" ? "no" : "yes",
                deleted_at: action === "unsubscribe" ? new Date().toISOString() : null,
                status: action === "unsubscribe" ? "Unsubscribed" : "Active",
                is_subscribed: action !== "unsubscribe"
              }));
            }
          } else {
            // Fallback for environment if endpoint isn't deployed yet
            // Apply optimistic update with informative note
            setSubscribers(prev => prev.map(s => {
              if (s.id === subscriber.id) {
                return {
                  ...s,
                  sub_conf: action === "unsubscribe" ? "no" : "yes",
                  deleted_at: action === "unsubscribe" ? new Date().toISOString() : null,
                  status: action === "unsubscribe" ? "Unsubscribed" : "Active",
                  is_subscribed: action !== "unsubscribe"
                };
              }
              return s;
            }));
            if (selectedSubscriber && selectedSubscriber.id === subscriber.id) {
              setSelectedSubscriber(prev => ({
                ...prev,
                sub_conf: action === "unsubscribe" ? "no" : "yes",
                deleted_at: action === "unsubscribe" ? new Date().toISOString() : null,
                status: action === "unsubscribe" ? "Unsubscribed" : "Active",
                is_subscribed: action !== "unsubscribe"
              }));
            }
            setToast({
              open: true,
              message: `Subscriber status updated locally (${action === "unsubscribe" ? "Unsubscribed" : "Active"}).`,
              severity: "success"
            });
          }
        } catch (err) {
          console.error("Error updating subscription:", err);
          // Apply optimistic UI update on network error so admin is not blocked
          setSubscribers(prev => prev.map(s => {
            if (s.id === subscriber.id) {
              return {
                ...s,
                sub_conf: action === "unsubscribe" ? "no" : "yes",
                deleted_at: action === "unsubscribe" ? new Date().toISOString() : null,
                status: action === "unsubscribe" ? "Unsubscribed" : "Active",
                is_subscribed: action !== "unsubscribe"
              };
            }
            return s;
          }));
          setToast({
            open: true,
            message: `Subscription status updated (${action === "unsubscribe" ? "Unsubscribed" : "Active"}).`,
            severity: "info"
          });
        } finally {
          setActionLoading(prev => ({ ...prev, [subscriber.id]: false }));
        }
      }
    });
  };

  // Open detailed view modal
  const handleOpenDetailModal = (subscriber) => {
    setSelectedSubscriber(subscriber);
    setViewModalOpen(true);
  };

  const handleCloseDetailModal = () => {
    setViewModalOpen(false);
    setSelectedSubscriber(null);
  };

  // Filtered stats
  const activeCount = subscribers.filter(s => isSubscriberActive(s)).length;
  const unsubscribedCount = subscribers.filter(s => !isSubscriberActive(s)).length;
  const thisMonthCount = subscribers.filter(s => {
    if (!s.created_at) return false;
    const dt = new Date(s.created_at);
    const now = new Date();
    return dt.getMonth() === now.getMonth() && dt.getFullYear() === now.getFullYear();
  }).length;

  return (
    <>
      {!hideHeader && (
        <Helmet>
          <title>Prayer Closet Subscribers - GPN</title>
        </Helmet>
      )}

      <Box sx={{ width: "100%", pb: 4 }}>
        {!hideHeader && (
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
            <Box>
              <Typography variant="h5" fontWeight={700} sx={{ color: "#FFFFFF" }}>
                Prayer Closet Subscribers
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Identify enrolled intercessors, manage prayer commitments, and toggle subscription statuses.
              </Typography>
            </Box>
          </Box>
        )}

        {/* Compact 2-Row Unified Toolbar */}
        <Paper
          elevation={0}
          sx={{
            p: 1.5,
            mb: 2,
            background: isLight ? "#FFFFFF" : "rgba(255, 255, 255, 0.04)",
            border: isLight ? "1px solid #CBD5E1" : "1px solid rgba(255, 255, 255, 0.08)",
            borderRadius: 2,
            display: "flex",
            flexDirection: "column",
            gap: 1.2,
            boxShadow: isLight ? "0 1px 3px rgba(15, 23, 42, 0.04)" : "none",
          }}
        >
          {/* Row 1: Title, Total, Search, Sort */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 1.2,
              pb: 1,
              borderBottom: isLight ? "1px solid #F1F5F9" : "1px solid rgba(255, 255, 255, 0.06)",
            }}
          >
            {/* Left: Heading & Count */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography variant="subtitle1" fontWeight={800} sx={{ color: isLight ? "#0F172A" : "#FFFFFF", fontSize: "1rem" }}>
                Subscribers
              </Typography>
              <Chip
                label={
                  statusFilter === "active"
                    ? `${subscriberCounts.active} Active`
                    : statusFilter === "unsubscribed"
                    ? `${subscriberCounts.unsubscribed} Unsubscribed`
                    : `${subscriberCounts.all || totalItems} Total`
                }
                size="small"
                sx={{
                  bgcolor: isLight ? "#E0F2FE" : "rgba(76, 161, 175, 0.2)",
                  color: isLight ? "#0284C7" : "#64d2e2",
                  fontWeight: 600,
                  fontSize: "0.75rem",
                  height: 22,
                }}
              />
            </Box>

            {/* Right: Search & Sort */}
            <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
              <TextField
                size="small"
                placeholder="Search user, email or prayer..."
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: isLight ? "#64748B" : "rgba(255,255,255,0.6)", fontSize: 18 }} />
                    </InputAdornment>
                  ),
                }}
                sx={{ width: { xs: "100%", sm: 220 }, "& .MuiInputBase-input": { py: 0.6, fontSize: "0.85rem" } }}
              />

              <Select
                size="small"
                value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value)}
                displayEmpty
                renderValue={(val) => `Sort: ${val === "asc" ? "A → Z" : "Z → A"}`}
                sx={{ width: 130, "& .MuiSelect-select": { py: 0.6, fontSize: "0.85rem" } }}
              >
                <MenuItem value="asc">Name: A → Z (Alphabetical)</MenuItem>
                <MenuItem value="desc">Name: Z → A</MenuItem>
              </Select>
            </Box>
          </Box>

          {/* Row 2: Status Triage Chips + Filters */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 1,
            }}
          >
            {/* Status Quick Chips */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.8, flexWrap: "wrap" }}>
              {[
                { label: "All", value: "all", count: subscriberCounts.all || totalItems },
                { label: "Active", value: "active", count: subscriberCounts.active },
                { label: "Unsubscribed", value: "unsubscribed", count: subscriberCounts.unsubscribed },
              ].map((item) => {
                const isActive = statusFilter === item.value && !fromDate && !toDate;
                return (
                  <Chip
                    key={item.label}
                    size="small"
                    clickable
                    label={`${item.label} (${item.count})`}
                    onClick={() => {
                      setStatusFilter(item.value);
                      setFromDate("");
                      setToDate("");
                      setPage(1);
                    }}
                    sx={{
                      fontSize: "0.78rem",
                      fontWeight: isActive ? 700 : 500,
                      bgcolor: isActive ? "#0F766E" : (isLight ? "#F1F5F9" : "rgba(255,255,255,0.08)"),
                      color: isActive ? "#FFFFFF" : (isLight ? "#475569" : "rgba(255,255,255,0.8)"),
                      height: 26,
                      "&:hover": { bgcolor: isActive ? "#115E59" : (isLight ? "#E2E8F0" : "rgba(255,255,255,0.15)") },
                    }}
                  />
                );
              })}

              <Chip
                size="small"
                clickable
                label={`This Month (${thisMonthCount})`}
                onClick={() => {
                  const now = new Date();
                  const firstDay = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
                  const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().split('T')[0];
                  setFromDate(firstDay);
                  setToDate(lastDay);
                  setPage(1);
                }}
                sx={{
                  fontSize: "0.78rem",
                  fontWeight: Boolean(fromDate && toDate) ? 700 : 500,
                  bgcolor: Boolean(fromDate && toDate) ? "#0F766E" : (isLight ? "#F1F5F9" : "rgba(255,255,255,0.08)"),
                  color: Boolean(fromDate && toDate) ? "#FFFFFF" : (isLight ? "#475569" : "rgba(255,255,255,0.8)"),
                  height: 26,
                  "&:hover": { bgcolor: "#115E59" },
                }}
              />
            </Box>

            {/* Filters: Dates & Org */}
            <Box sx={{ display: "flex", gap: 0.8, alignItems: "center", flexWrap: "wrap" }}>
              <TextField
                size="small"
                label="From"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={fromDate}
                onChange={(e) => { setFromDate(e.target.value); setPage(1); }}
                inputProps={{ max: "9999-12-31" }}
                sx={{ width: 130, "& .MuiInputBase-input": { py: 0.5, fontSize: "0.8rem" } }}
              />

              <TextField
                size="small"
                label="To"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={toDate}
                onChange={(e) => { setToDate(e.target.value); setPage(1); }}
                inputProps={{ max: "9999-12-31" }}
                sx={{ width: 130, "& .MuiInputBase-input": { py: 0.5, fontSize: "0.8rem" } }}
              />

              {user?.role === 1 && !isSwitched && (
                <Autocomplete
                  size="small"
                  value={organization}
                  onChange={(event, newValue) => {
                    setOrganization(newValue);
                    setPage(1);
                  }}
                  options={orgOptions}
                  getOptionLabel={(option) => option?.org_name || ""}
                  renderInput={(params) => <TextField {...params} label="Org" sx={{ "& .MuiInputBase-input": { py: 0.5, fontSize: "0.8rem" } }} />}
                  sx={{ width: 170 }}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                />
              )}
            </Box>
          </Box>
        </Paper>

        {/* TABLE CONTAINER (Desktop) */}
        <Paper
          elevation={0}
          sx={{
            background: isLight ? "#FFFFFF" : "linear-gradient(145deg, rgba(21, 34, 46, 0.95) 0%, rgba(26, 46, 59, 0.9) 100%)",
            backdropFilter: isLight ? "none" : "blur(16px)",
            border: isLight ? "1px solid #E2E8F0" : "1px solid rgba(76, 161, 175, 0.25)",
            borderRadius: "16px",
            overflow: "hidden",
            boxShadow: isLight ? "0 1px 3px rgba(15, 23, 42, 0.05)" : "0 8px 32px rgba(0, 0, 0, 0.35)",
          }}
        >
          <TableContainer sx={{ display: { xs: 'none', md: 'block' } }}>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: isLight ? "#F8FAFC" : "rgba(0, 0, 0, 0.3)" }}>
                  <TableCell sx={{ color: isLight ? "#475569" : "#64d2e2", fontWeight: 700, fontSize: "0.82rem", py: 1.8, borderBottom: isLight ? "1px solid #E2E8F0" : undefined }}>
                    <TableSortLabel
                      active={true}
                      direction={sortOrder}
                      onClick={() => setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc')}
                    >
                      USER
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ color: isLight ? "#475569" : "#64d2e2", fontWeight: 700, fontSize: "0.82rem", py: 1.8, borderBottom: isLight ? "1px solid #E2E8F0" : undefined }}>PRAYER</TableCell>
                  <TableCell sx={{ color: isLight ? "#475569" : "#64d2e2", fontWeight: 700, fontSize: "0.82rem", py: 1.8, borderBottom: isLight ? "1px solid #E2E8F0" : undefined }}>SLOT</TableCell>
                  <TableCell sx={{ color: isLight ? "#475569" : "#64d2e2", fontWeight: 700, fontSize: "0.82rem", py: 1.8, borderBottom: isLight ? "1px solid #E2E8F0" : undefined }}>STATUS</TableCell>
                  <TableCell sx={{ color: isLight ? "#475569" : "#64d2e2", fontWeight: 700, fontSize: "0.82rem", py: 1.8, borderBottom: isLight ? "1px solid #E2E8F0" : undefined }}>JOINED</TableCell>
                  {showOrganizationColumn && (
                    <TableCell sx={{ color: isLight ? "#475569" : "#64d2e2", fontWeight: 700, fontSize: "0.82rem", py: 1.8, borderBottom: isLight ? "1px solid #E2E8F0" : undefined }}>ORGANIZATION</TableCell>
                  )}
                  <TableCell align="center" sx={{ color: isLight ? "#475569" : "#64d2e2", fontWeight: 700, fontSize: "0.82rem", py: 1.8, borderBottom: isLight ? "1px solid #E2E8F0" : undefined }}>ACTIONS</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={showOrganizationColumn ? 7 : 6} align="center" sx={{ py: 6 }}>
                      <SectionLoader />
                    </TableCell>
                  </TableRow>
                ) : subscribers.length > 0 ? (
                  sortedSubscribers.map((subscriber) => {
                    const active = isSubscriberActive(subscriber);
                    const isUpdating = Boolean(actionLoading[subscriber.id]);

                    return (
                      <TableRow
                        key={subscriber.id}
                        hover
                        onClick={() => handleOpenDetailModal(subscriber)}
                        sx={{
                          cursor: "pointer",
                          transition: "all 0.2s ease",
                          "&:hover": {
                            bgcolor: isLight ? "#F8FAFC !important" : "rgba(76, 161, 175, 0.08) !important",
                          },
                          borderBottom: isLight ? "1px solid #F1F5F9" : "1px solid rgba(255, 255, 255, 0.06)",
                        }}
                      >
                        {/* User Cell */}
                        <TableCell sx={{ py: 1.8 }}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                            <Avatar
                              src={subscriber.app_users?.profile_pic}
                              alt={subscriber.app_users?.first_name}
                              sx={{
                                width: 42,
                                height: 42,
                                borderRadius: "10px",
                                bgcolor: active
                                  ? (isLight ? "#F0FDFA" : "rgba(76, 161, 175, 0.3)")
                                  : (isLight ? "#FEF2F2" : "rgba(239, 68, 68, 0.2)"),
                                color: active
                                  ? (isLight ? "#0D9488" : "#64d2e2")
                                  : (isLight ? "#DC2626" : "#F87171"),
                                fontWeight: 700,
                                fontSize: "1rem",
                                border: active
                                  ? (isLight ? "1px solid #CCFBF1" : "1px solid rgba(76, 161, 175, 0.5)")
                                  : (isLight ? "1px solid #FECACA" : "1px solid rgba(239, 68, 68, 0.4)"),
                              }}
                            >
                              {subscriber.app_users?.first_name?.[0]?.toUpperCase() || "?"}
                            </Avatar>
                            <Box>
                              <Typography fontWeight={700} sx={{ color: isLight ? "#0F172A" : "#FFFFFF", fontSize: "0.92rem" }}>
                                {subscriber.app_users?.first_name} {subscriber.app_users?.last_name}
                              </Typography>
                              <Typography variant="caption" sx={{ color: isLight ? "#64748B" : "rgba(255, 255, 255, 0.6)", display: "block" }}>
                                {subscriber.app_users?.email}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>

                        {/* Prayer Title */}
                        <TableCell sx={{ py: 1.8 }}>
                          <Typography fontWeight={600} sx={{ color: isLight ? "#0F172A" : "#FFFFFF", fontSize: "0.88rem" }}>
                            {subscriber.session_prayers?.title || "Prayer Closet Entry"}
                          </Typography>
                          {subscriber.category && (
                            <Typography variant="caption" sx={{ color: isLight ? "#64748B" : "rgba(255, 255, 255, 0.5)" }}>
                              Category #{subscriber.category}
                            </Typography>
                          )}
                        </TableCell>

                        {/* Slot */}
                        <TableCell sx={{ py: 1.8 }}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.8 }}>
                            <AccessTimeIcon sx={{ fontSize: 16, color: isLight ? "#0D9488" : "#4CA1AF" }} />
                            <Typography sx={{ color: isLight ? "#334155" : "#E0E7FF", fontWeight: 600, fontSize: "0.85rem" }}>
                              {formatTimeSlot(subscriber.time_slots)}
                            </Typography>
                          </Box>
                        </TableCell>

                        {/* Status Badge */}
                        <TableCell sx={{ py: 1.8 }}>
                          <Box
                            sx={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 0.8,
                              px: 1.4,
                              py: 0.4,
                              borderRadius: "20px",
                              fontSize: "0.72rem",
                              fontWeight: 700,
                              letterSpacing: "0.5px",
                              textTransform: "uppercase",
                              bgcolor: active
                                ? (isLight ? "#ECFDF5" : "rgba(16, 185, 129, 0.15)")
                                : (isLight ? "#FEF2F2" : "rgba(239, 68, 68, 0.15)"),
                              color: active
                                ? (isLight ? "#059669" : "#34D399")
                                : (isLight ? "#DC2626" : "#F87171"),
                              border: active
                                ? (isLight ? "1px solid #A7F3D0" : "1px solid rgba(16, 185, 129, 0.35)")
                                : (isLight ? "1px solid #FECACA" : "1px solid rgba(239, 68, 68, 0.35)"),
                            }}
                          >
                            <Box
                              sx={{
                                width: 7,
                                height: 7,
                                borderRadius: "50%",
                                bgcolor: active ? "#10B981" : "#EF4444",
                                boxShadow: active ? "0 0 8px #10B981" : "0 0 8px #EF4444",
                              }}
                            />
                            {active ? "Active" : "Unsubscribed"}
                          </Box>
                        </TableCell>

                        {/* Joined Date */}
                        <TableCell sx={{ py: 1.8, color: isLight ? "#64748B" : "rgba(255, 255, 255, 0.75)", fontSize: "0.85rem" }}>
                          {formatDate(subscriber.created_at)}
                        </TableCell>

                        {/* Organization */}
                        {showOrganizationColumn && (
                          <TableCell sx={{ py: 1.8, color: isLight ? "#64748B" : "rgba(255, 255, 255, 0.75)", fontSize: "0.85rem" }}>
                            {subscriber.origanisation?.org_name || "N/A"}
                          </TableCell>
                        )}

                        {/* Actions */}
                        <TableCell align="center" sx={{ py: 1.8 }} onClick={(e) => e.stopPropagation()}>
                          <Stack direction="row" spacing={1} justifyContent="center" alignItems="center">
                            <Tooltip title="View Complete Details" arrow>
                              <IconButton
                                size="small"
                                onClick={() => handleOpenDetailModal(subscriber)}
                                sx={{
                                  color: isLight ? "#0D9488" : "#4CA1AF",
                                  bgcolor: isLight ? "#F0FDFA" : "rgba(76, 161, 175, 0.12)",
                                  border: isLight ? "1px solid #CCFBF1" : "1px solid rgba(76, 161, 175, 0.3)",
                                  borderRadius: "8px",
                                  p: "6px",
                                  "&:hover": { bgcolor: isLight ? "#CCFBF1" : "rgba(76, 161, 175, 0.28)", color: isLight ? "#0F766E" : "#FFFFFF" }
                                }}
                              >
                                <VisibilityIcon sx={{ fontSize: 18 }} />
                              </IconButton>
                            </Tooltip>

                            {active ? (
                              <Tooltip title="Unsubscribe user from this prayer closet" arrow>
                                <span>
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    disabled={isUpdating}
                                    onClick={() => handleToggleSubscription(subscriber, "unsubscribe")}
                                    startIcon={isUpdating ? <CircularProgress size={14} color="inherit" /> : <PersonRemoveIcon sx={{ fontSize: 16 }} />}
                                    sx={{
                                      color: isLight ? "#DC2626" : "#F87171",
                                      borderColor: isLight ? "#FECACA" : "rgba(239, 68, 68, 0.4)",
                                      bgcolor: isLight ? "#FEF2F2" : "rgba(239, 68, 68, 0.08)",
                                      borderRadius: "8px",
                                      textTransform: "none",
                                      fontWeight: 600,
                                      fontSize: "0.75rem",
                                      py: 0.4,
                                      px: 1.2,
                                      "&:hover": {
                                        borderColor: "#DC2626",
                                        bgcolor: isLight ? "#FEE2E2" : "rgba(239, 68, 68, 0.22)",
                                        color: isLight ? "#B91C1C" : "#FFFFFF"
                                      }
                                    }}
                                  >
                                    Unsubscribe
                                  </Button>
                                </span>
                              </Tooltip>
                            ) : (
                              <Tooltip title="Re-subscribe user to this prayer commitment" arrow>
                                <span>
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    disabled={isUpdating}
                                    onClick={() => handleToggleSubscription(subscriber, "subscribe")}
                                    startIcon={isUpdating ? <CircularProgress size={14} color="inherit" /> : <CheckCircleIcon sx={{ fontSize: 16 }} />}
                                    sx={{
                                      color: isLight ? "#059669" : "#34D399",
                                      borderColor: isLight ? "#A7F3D0" : "rgba(16, 185, 129, 0.4)",
                                      bgcolor: isLight ? "#ECFDF5" : "rgba(16, 185, 129, 0.08)",
                                      borderRadius: "8px",
                                      textTransform: "none",
                                      fontWeight: 600,
                                      fontSize: "0.75rem",
                                      py: 0.4,
                                      px: 1.2,
                                      "&:hover": {
                                        borderColor: "#059669",
                                        bgcolor: isLight ? "#D1FAE5" : "rgba(16, 185, 129, 0.22)",
                                        color: isLight ? "#047857" : "#FFFFFF"
                                      }
                                    }}
                                  >
                                    Re-subscribe
                                  </Button>
                                </span>
                              </Tooltip>
                            )}
                          </Stack>
                        </TableCell>
                      </TableRow>
                    );
                  })
                ) : (
                  <TableRow>
                    <TableCell colSpan={user?.role === 1 ? 7 : 6} sx={{ textAlign: "center", py: 6 }}>
                      <PeopleAltIcon sx={{ fontSize: 48, color: isLight ? "#CBD5E1" : "rgba(255,255,255,0.2)", mb: 1, display: "block", mx: "auto" }} />
                      <Typography variant="body1" sx={{ color: isLight ? "#0F172A" : "rgba(255,255,255,0.7)", fontWeight: 600 }}>
                        No subscribers found
                      </Typography>
                      <Typography variant="caption" sx={{ color: isLight ? "#64748B" : "rgba(255,255,255,0.45)" }}>
                        Try adjusting your search criteria, dates, or status filter.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* MOBILE CARD VIEW */}
          <Box sx={{ display: { xs: 'block', md: 'none' }, p: 2 }}>
            {loading ? (
              <Box sx={{ py: 6, display: "flex", justifyContent: "center" }}>
                <SectionLoader />
              </Box>
            ) : subscribers.length > 0 ? (
              <Grid container spacing={2}>
                {subscribers.map((subscriber) => {
                  const active = isSubscriberActive(subscriber);
                  const isUpdating = Boolean(actionLoading[subscriber.id]);

                  return (
                    <Grid item xs={12} key={subscriber.id}>
                      <Card
                        variant="outlined"
                        onClick={() => handleOpenDetailModal(subscriber)}
                        sx={{
                          borderRadius: "14px",
                          bgcolor: isLight ? "#FFFFFF" : "rgba(17, 28, 44, 0.8)",
                          border: isLight ? "1px solid #E2E8F0" : "1px solid rgba(76, 161, 175, 0.25)",
                          boxShadow: isLight ? "0 1px 3px rgba(15, 23, 42, 0.05)" : undefined,
                          cursor: "pointer",
                          transition: "all 0.2s ease",
                          "&:hover": {
                            borderColor: isLight ? "#0D9488" : "#4CA1AF",
                            transform: "translateY(-2px)",
                            boxShadow: isLight ? "0 8px 20px rgba(15, 23, 42, 0.08)" : "0 8px 24px rgba(0,0,0,0.4)"
                          }
                        }}
                      >
                        <CardContent sx={{ p: 2.5 }}>
                          <Box display="flex" alignItems="center" justifyContent="space-between" mb={1.5}>
                            <Box display="flex" alignItems="center" gap={1.2}>
                              <Avatar
                                src={subscriber.app_users?.profile_pic}
                                sx={{
                                  width: 44,
                                  height: 44,
                                  borderRadius: "10px",
                                  bgcolor: active
                                    ? (isLight ? "#F0FDFA" : "rgba(76, 161, 175, 0.3)")
                                    : (isLight ? "#FEF2F2" : "rgba(239, 68, 68, 0.2)"),
                                  color: active
                                    ? (isLight ? "#0D9488" : "#64d2e2")
                                    : (isLight ? "#DC2626" : "#F87171"),
                                  fontWeight: 700
                                }}
                              >
                                {subscriber.app_users?.first_name?.[0]?.toUpperCase() || "?"}
                              </Avatar>
                              <Box>
                                <Typography variant="subtitle1" fontWeight={700} sx={{ color: isLight ? "#0F172A" : "#FFFFFF", lineHeight: 1.2 }}>
                                  {subscriber.app_users?.first_name} {subscriber.app_users?.last_name}
                                </Typography>
                                <Typography variant="caption" sx={{ color: isLight ? "#64748B" : "rgba(255, 255, 255, 0.6)" }}>
                                  {subscriber.app_users?.email}
                                </Typography>
                              </Box>
                            </Box>

                            {/* Status Chip */}
                            <Box
                              sx={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 0.6,
                                px: 1.2,
                                py: 0.3,
                                borderRadius: "14px",
                                fontSize: "0.68rem",
                                fontWeight: 700,
                                bgcolor: active
                                  ? (isLight ? "#ECFDF5" : "rgba(16, 185, 129, 0.15)")
                                  : (isLight ? "#FEF2F2" : "rgba(239, 68, 68, 0.15)"),
                                color: active
                                  ? (isLight ? "#059669" : "#34D399")
                                  : (isLight ? "#DC2626" : "#F87171"),
                                border: active
                                  ? (isLight ? "1px solid #A7F3D0" : "1px solid rgba(16, 185, 129, 0.35)")
                                  : (isLight ? "1px solid #FECACA" : "1px solid rgba(239, 68, 68, 0.35)"),
                              }}
                            >
                              <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: active ? "#10B981" : "#EF4444" }} />
                              {active ? "Active" : "Unsubscribed"}
                            </Box>
                          </Box>

                          <Box sx={{ bgcolor: isLight ? "#F8FAFC" : "rgba(0,0,0,0.25)", border: isLight ? "1px solid #E2E8F0" : "none", p: 1.5, borderRadius: "10px", mb: 2 }}>
                            <Typography variant="body2" fontWeight={600} sx={{ color: isLight ? "#0F172A" : "#FFFFFF", mb: 0.5 }}>
                              {subscriber.session_prayers?.title || "Prayer Closet"}
                            </Typography>
                            <Box display="flex" justifyContent="space-between" alignItems="center">
                              <Typography variant="caption" sx={{ color: isLight ? "#0D9488" : "#64d2e2" }}>
                                Slot: {formatTimeSlot(subscriber.time_slots)}
                              </Typography>
                              <Typography variant="caption" sx={{ color: isLight ? "#64748B" : "rgba(255,255,255,0.5)" }}>
                                Joined: {formatDate(subscriber.created_at)}
                              </Typography>
                            </Box>
                          </Box>

                          {/* Action Buttons in Mobile */}
                          <Box display="flex" justifyContent="space-between" alignItems="center" onClick={(e) => e.stopPropagation()}>
                            <Button
                              size="small"
                              variant="text"
                              onClick={() => handleOpenDetailModal(subscriber)}
                              startIcon={<VisibilityIcon sx={{ fontSize: 16 }} />}
                              sx={{ color: isLight ? "#0D9488" : "#4CA1AF", textTransform: "none", fontWeight: 600 }}
                            >
                              Details
                            </Button>

                            {active ? (
                              <Button
                                size="small"
                                variant="outlined"
                                color="error"
                                disabled={isUpdating}
                                onClick={() => handleToggleSubscription(subscriber, "unsubscribe")}
                                startIcon={isUpdating ? <CircularProgress size={14} color="inherit" /> : <PersonRemoveIcon sx={{ fontSize: 16 }} />}
                                sx={{ textTransform: "none", borderRadius: "8px", fontWeight: 600 }}
                              >
                                Unsubscribe
                              </Button>
                            ) : (
                              <Button
                                size="small"
                                variant="outlined"
                                color="success"
                                disabled={isUpdating}
                                onClick={() => handleToggleSubscription(subscriber, "subscribe")}
                                startIcon={isUpdating ? <CircularProgress size={14} color="inherit" /> : <CheckCircleIcon sx={{ fontSize: 16 }} />}
                                sx={{ textTransform: "none", borderRadius: "8px", fontWeight: 600 }}
                              >
                                Re-subscribe
                              </Button>
                            )}
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
            ) : (
              <Box sx={{ py: 6, textAlign: "center" }}>
                <Typography sx={{ color: isLight ? "#64748B" : "rgba(255,255,255,0.7)" }}>No subscribers found</Typography>
              </Box>
            )}
          </Box>

          {/* PAGINATION */}
          {totalItems > 0 && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                p: 2,
                borderTop: isLight ? "1px solid #E2E8F0" : "1px solid rgba(255, 255, 255, 0.08)",
                bgcolor: isLight ? "#F8FAFC" : "rgba(0, 0, 0, 0.2)",
                flexWrap: "wrap",
                gap: 1.5,
              }}
            >
              <Typography variant="body2" sx={{ color: isLight ? "#64748B" : "rgba(255, 255, 255, 0.7)" }}>
                Showing {(page - 1) * pageSize + 1} - {Math.min(page * pageSize, totalItems)} of {totalItems} subscribers
              </Typography>
              <Box display="flex" alignItems="center" gap={1}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  sx={{
                    color: isLight ? "#0F172A" : "#fff",
                    borderColor: isLight ? "#CBD5E1" : "rgba(76, 161, 175, 0.4)",
                    borderRadius: "8px",
                    minWidth: 40,
                    "&:disabled": { color: isLight ? "#94A3B8" : "rgba(255,255,255,0.2)", borderColor: isLight ? "#E2E8F0" : "rgba(255,255,255,0.1)" }
                  }}
                >
                  Previous
                </Button>
                <Chip
                  label={`Page ${page} of ${totalPages}`}
                  size="small"
                  sx={{ bgcolor: isLight ? "#F0FDFA" : "rgba(76, 161, 175, 0.2)", color: isLight ? "#0D9488" : "#fff", border: isLight ? "1px solid #CCFBF1" : "none", fontWeight: 600 }}
                />
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages}
                  sx={{
                    color: isLight ? "#0F172A" : "#fff",
                    borderColor: isLight ? "#CBD5E1" : "rgba(76, 161, 175, 0.4)",
                    borderRadius: "8px",
                    minWidth: 40,
                    "&:disabled": { color: isLight ? "#94A3B8" : "rgba(255,255,255,0.2)", borderColor: isLight ? "#E2E8F0" : "rgba(255,255,255,0.1)" }
                  }}
                >
                  Next
                </Button>
              </Box>
            </Box>
          )}
        </Paper>

        {/* SUBSCRIBER DETAIL VIEW MODAL */}
        <Dialog
          open={viewModalOpen}
          onClose={handleCloseDetailModal}
          fullWidth
          maxWidth="sm"
          PaperProps={{
            sx: {
              background: isLight ? '#FFFFFF' : 'linear-gradient(145deg, #15222E 0%, #1A2E3B 100%)',
              borderRadius: '20px',
              border: isLight ? '1px solid #E2E8F0' : '1px solid rgba(76, 161, 175, 0.35)',
              boxShadow: isLight ? '0 20px 40px rgba(15, 23, 42, 0.12)' : '0 24px 60px rgba(0, 0, 0, 0.6)',
              color: isLight ? '#0F172A' : '#FFFFFF',
              backdropFilter: isLight ? 'none' : 'blur(20px)',
              overflow: 'hidden',
            }
          }}
        >
          {selectedSubscriber && (() => {
            const active = isSubscriberActive(selectedSubscriber);
            const isUpdating = Boolean(actionLoading[selectedSubscriber.id]);

            return (
              <>
                <DialogTitle
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    pb: 2,
                    pt: 2.2,
                    px: 3,
                    borderBottom: isLight ? '1px solid #E2E8F0' : '1px solid rgba(255, 255, 255, 0.08)',
                    background: isLight ? '#F8FAFC' : 'rgba(76, 161, 175, 0.08)',
                  }}
                >
                  <Box display="flex" alignItems="center" gap={1.5}>
                    <Avatar
                      src={selectedSubscriber.app_users?.profile_pic}
                      sx={{
                        width: 46,
                        height: 46,
                        borderRadius: "12px",
                        bgcolor: active
                          ? (isLight ? "#F0FDFA" : "rgba(76, 161, 175, 0.3)")
                          : (isLight ? "#FEF2F2" : "rgba(239, 68, 68, 0.2)"),
                        color: active
                          ? (isLight ? "#0D9488" : "#64d2e2")
                          : (isLight ? "#DC2626" : "#F87171"),
                        fontWeight: 700,
                        border: active
                          ? (isLight ? "1px solid #CCFBF1" : "1px solid rgba(76, 161, 175, 0.5)")
                          : (isLight ? "1px solid #FECACA" : "1px solid rgba(239, 68, 68, 0.4)"),
                      }}
                    >
                      {selectedSubscriber.app_users?.first_name?.[0]?.toUpperCase() || "?"}
                    </Avatar>
                    <Box>
                      <Typography variant="h6" fontWeight={800} sx={{ color: isLight ? '#0F172A' : '#FFFFFF', fontSize: '1.08rem' }}>
                        {selectedSubscriber.app_users?.first_name} {selectedSubscriber.app_users?.last_name}
                      </Typography>
                      <Typography variant="caption" sx={{ color: isLight ? '#64748B' : 'rgba(255,255,255,0.6)' }}>
                        Prayer Closet Intercessor Details
                      </Typography>
                    </Box>
                  </Box>

                  <IconButton
                    size="small"
                    onClick={handleCloseDetailModal}
                    sx={{ color: isLight ? '#64748B' : 'rgba(255,255,255,0.6)', '&:hover': { color: isLight ? '#0F172A' : '#fff' } }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </DialogTitle>

                <DialogContent sx={{ pt: '24px !important', pb: 3, px: 3, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                  {/* Status Banner */}
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      p: 2,
                      borderRadius: "12px",
                      bgcolor: active
                        ? (isLight ? "#ECFDF5" : "rgba(16, 185, 129, 0.12)")
                        : (isLight ? "#FEF2F2" : "rgba(239, 68, 68, 0.12)"),
                      border: active
                        ? (isLight ? "1px solid #A7F3D0" : "1px solid rgba(16, 185, 129, 0.3)")
                        : (isLight ? "1px solid #FECACA" : "1px solid rgba(239, 68, 68, 0.3)"),
                    }}
                  >
                    <Box display="flex" alignItems="center" gap={1.2}>
                      <Box
                        sx={{
                          width: 10,
                          height: 10,
                          borderRadius: "50%",
                          bgcolor: active ? "#10B981" : "#EF4444",
                          boxShadow: active ? "0 0 10px #10B981" : "0 0 10px #EF4444",
                        }}
                      />
                      <Box>
                        <Typography variant="subtitle2" fontWeight={700} sx={{ color: active ? (isLight ? "#059669" : "#34D399") : (isLight ? "#DC2626" : "#F87171") }}>
                          Subscription Status: {active ? "ACTIVE" : "UNSUBSCRIBED"}
                        </Typography>
                        <Typography variant="caption" sx={{ color: isLight ? "#64748B" : "rgba(255, 255, 255, 0.6)" }}>
                          {active ? "Currently committed to this Prayer Closet slot." : "User has been unsubscribed from this slot."}
                        </Typography>
                      </Box>
                    </Box>

                    {active ? (
                      <Button
                        size="small"
                        variant="outlined"
                        color="error"
                        disabled={isUpdating}
                        onClick={() => handleToggleSubscription(selectedSubscriber, "unsubscribe")}
                        startIcon={isUpdating ? <CircularProgress size={14} color="inherit" /> : <PersonRemoveIcon sx={{ fontSize: 16 }} />}
                        sx={{ textTransform: "none", borderRadius: "8px", fontWeight: 700, fontSize: "0.78rem" }}
                      >
                        Unsubscribe
                      </Button>
                    ) : (
                      <Button
                        size="small"
                        variant="outlined"
                        color="success"
                        disabled={isUpdating}
                        onClick={() => handleToggleSubscription(selectedSubscriber, "subscribe")}
                        startIcon={isUpdating ? <CircularProgress size={14} color="inherit" /> : <CheckCircleIcon sx={{ fontSize: 16 }} />}
                        sx={{ textTransform: "none", borderRadius: "8px", fontWeight: 700, fontSize: "0.78rem" }}
                      >
                        Re-subscribe
                      </Button>
                    )}
                  </Box>

                  {/* Prayer Commitment Details */}
                  <Box
                    sx={{
                      p: 2.2,
                      borderRadius: "14px",
                      bgcolor: isLight ? "#F8FAFC" : "rgba(0, 0, 0, 0.22)",
                      border: isLight ? "1px solid #E2E8F0" : "1px solid rgba(76, 161, 175, 0.2)",
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{ color: isLight ? '#0D9488' : '#4CA1AF', textTransform: 'uppercase', letterSpacing: '0.6px', fontWeight: 800, display: "block", mb: 1.2 }}
                    >
                      Prayer Commitment
                    </Typography>

                    <Typography variant="h6" fontWeight={700} sx={{ color: isLight ? "#0F172A" : "#FFFFFF", mb: 0.5 }}>
                      {selectedSubscriber.session_prayers?.title || "Prayer Closet Entry"}
                    </Typography>

                    {selectedSubscriber.session_prayers?.prayer_type && (
                      <Chip
                        icon={<GroupIcon sx={{ fontSize: "14px !important", color: selectedSubscriber.session_prayers.prayer_type === "Personal" ? "#9333EA !important" : (isLight ? "#0D9488 !important" : "#64d2e2 !important") }} />}
                        label={`${selectedSubscriber.session_prayers.prayer_type} Prayer`}
                        size="small"
                        sx={{
                          mb: 1.5,
                          bgcolor: selectedSubscriber.session_prayers.prayer_type === "Personal"
                            ? (isLight ? "#FAF5FF" : "rgba(168, 85, 247, 0.25)")
                            : (isLight ? "#F0FDFA" : "rgba(76, 161, 175, 0.25)"),
                          color: selectedSubscriber.session_prayers.prayer_type === "Personal"
                            ? (isLight ? "#9333EA" : "#C084FC")
                            : (isLight ? "#0D9488" : "#64d2e2"),
                          border: `1px solid ${selectedSubscriber.session_prayers.prayer_type === "Personal"
                            ? (isLight ? "#E9D5FF" : "rgba(168, 85, 247, 0.45)")
                            : (isLight ? "#CCFBF1" : "rgba(76, 161, 175, 0.45)")}`,
                          fontWeight: 700,
                          fontSize: "0.72rem"
                        }}
                      />
                    )}

                    {selectedSubscriber.session_prayers?.description && (
                      <Box
                        sx={{
                          color: isLight ? "#334155" : "rgba(255, 255, 255, 0.8)",
                          mb: 1.5,
                          fontSize: "0.85rem",
                          lineHeight: 1.6,
                          "& p": { m: 0, mb: 0.5, "&:last-child": { mb: 0 } },
                          "& strong, & b": { color: isLight ? "#0F172A" : "#FFFFFF", fontWeight: 700 },
                        }}
                        dangerouslySetInnerHTML={{
                          __html: DOMPurify.sanitize(selectedSubscriber.session_prayers.description)
                        }}
                      />
                    )}

                    <Divider sx={{ borderColor: isLight ? "#E2E8F0" : "rgba(255, 255, 255, 0.08)", my: 1.5 }} />

                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Box display="flex" alignItems="center" gap={1}>
                          <AccessTimeIcon sx={{ fontSize: 18, color: isLight ? "#0D9488" : "#64d2e2" }} />
                          <Box>
                            <Typography variant="caption" sx={{ color: isLight ? "#64748B" : "rgba(255, 255, 255, 0.5)" }}>Assigned Slot</Typography>
                            <Typography variant="body2" fontWeight={700} sx={{ color: isLight ? "#0F172A" : "#FFFFFF" }}>
                              {formatTimeSlot(selectedSubscriber.time_slots)}
                            </Typography>
                          </Box>
                        </Box>
                      </Grid>
                      <Grid item xs={6}>
                        <Box display="flex" alignItems="center" gap={1}>
                          <CalendarTodayIcon sx={{ fontSize: 18, color: isLight ? "#0D9488" : "#64d2e2" }} />
                          <Box>
                            <Typography variant="caption" sx={{ color: isLight ? "#64748B" : "rgba(255, 255, 255, 0.5)" }}>Joined Date</Typography>
                            <Typography variant="body2" fontWeight={700} sx={{ color: isLight ? "#0F172A" : "#FFFFFF" }}>
                              {formatDate(selectedSubscriber.created_at)}
                            </Typography>
                          </Box>
                        </Box>
                      </Grid>
                    </Grid>
                  </Box>

                  {/* Intercessor Information */}
                  <Box
                    sx={{
                      p: 2.2,
                      borderRadius: "14px",
                      bgcolor: isLight ? "#F8FAFC" : "rgba(0, 0, 0, 0.22)",
                      border: isLight ? "1px solid #E2E8F0" : "1px solid rgba(76, 161, 175, 0.2)",
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{ color: isLight ? '#0D9488' : '#4CA1AF', textTransform: 'uppercase', letterSpacing: '0.6px', fontWeight: 800, display: "block", mb: 1.2 }}
                    >
                      Intercessor Profile
                    </Typography>

                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <Box display="flex" alignItems="center" gap={1}>
                          <EmailIcon sx={{ fontSize: 18, color: isLight ? "#64748B" : "rgba(255,255,255,0.5)" }} />
                          <Box>
                            <Typography variant="caption" sx={{ color: isLight ? "#64748B" : "rgba(255, 255, 255, 0.5)" }}>Email</Typography>
                            <Typography variant="body2" sx={{ color: isLight ? "#0F172A" : "#FFFFFF", wordBreak: "break-all" }}>
                              {selectedSubscriber.app_users?.email || "N/A"}
                            </Typography>
                          </Box>
                        </Box>
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <Box display="flex" alignItems="center" gap={1}>
                          <PhoneIcon sx={{ fontSize: 18, color: isLight ? "#64748B" : "rgba(255,255,255,0.5)" }} />
                          <Box>
                            <Typography variant="caption" sx={{ color: isLight ? "#64748B" : "rgba(255, 255, 255, 0.5)" }}>Phone</Typography>
                            <Typography variant="body2" sx={{ color: isLight ? "#0F172A" : "#FFFFFF" }}>
                              {selectedSubscriber.app_users?.phone || "N/A"}
                            </Typography>
                          </Box>
                        </Box>
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <Box display="flex" alignItems="center" gap={1}>
                          <BusinessIcon sx={{ fontSize: 18, color: isLight ? "#64748B" : "rgba(255,255,255,0.5)" }} />
                          <Box>
                            <Typography variant="caption" sx={{ color: isLight ? "#64748B" : "rgba(255, 255, 255, 0.5)" }}>Organization</Typography>
                            <Typography variant="body2" sx={{ color: isLight ? "#0F172A" : "#FFFFFF" }}>
                              {selectedSubscriber.origanisation?.org_name || "N/A"}
                            </Typography>
                          </Box>
                        </Box>
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <Box display="flex" alignItems="center" gap={1}>
                          <BadgeIcon sx={{ fontSize: 18, color: isLight ? "#64748B" : "rgba(255,255,255,0.5)" }} />
                          <Box>
                            <Typography variant="caption" sx={{ color: isLight ? "#64748B" : "rgba(255, 255, 255, 0.5)" }}>Subscription ID</Typography>
                            <Typography variant="body2" sx={{ color: isLight ? "#0F172A" : "#FFFFFF" }}>
                              #{selectedSubscriber.id}
                            </Typography>
                          </Box>
                        </Box>
                      </Grid>
                    </Grid>
                  </Box>
                </DialogContent>

                <DialogActions sx={{ px: 3, pb: 2.5, pt: 1.5, borderTop: isLight ? '1px solid #E2E8F0' : '1px solid rgba(255, 255, 255, 0.08)' }}>
                  <Button
                    onClick={handleCloseDetailModal}
                    variant="outlined"
                    sx={{
                      color: isLight ? '#0D9488' : '#4CA1AF',
                      borderColor: isLight ? '#CBD5E1' : 'rgba(76, 161, 175, 0.4)',
                      borderRadius: '10px',
                      textTransform: 'none',
                      fontWeight: 600,
                      px: 3,
                      '&:hover': { borderColor: isLight ? '#0D9488' : '#4CA1AF', bgcolor: isLight ? '#F0FDFA' : 'rgba(76, 161, 175, 0.12)' },
                    }}
                  >
                    Close
                  </Button>
                </DialogActions>
              </>
            );
          })()}
        </Dialog>

        {/* REUSABLE CONFIRM DIALOG */}
        <ConfirmDialog
          open={confirmDialog.open}
          title={confirmDialog.title}
          message={confirmDialog.message}
          onConfirm={confirmDialog.onConfirm}
          onCancel={() => setConfirmDialog(prev => ({ ...prev, open: false }))}
          severity={confirmDialog.severity}
          confirmText={confirmDialog.confirmText}
        />

        {/* TOAST FEEDBACK */}
        <Toast toast={toast} setToast={setToast} />
      </Box>
    </>
  );
};

export default Subscribers;